import { Button } from '@/components/shadcn/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/shadcn/tabs.tsx'
import useUserPermissions from '@/hooks/useUserPermissions'
import { featureFlagKeys } from '@/utils/data/featureFlags'
import {
  DROP_SHIPPER,
  FREE_PLAN,
  PRO_PLAN,
  PlanDetail,
  SPARK_PLAN,
  STANDARD_PLAN,
  SUBSCRIPTION_STATUSES,
  VISION_PASS,
  plans,
} from '@/utils/data/plans.ts'
import { trpc } from '@/utils/trpc'
import { Message } from '@mynaui/icons-react'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useCallback, useState } from 'react'
import UpgradeBox from './UpgradeBox'
import { Stack, Text } from './custom-components'
import { Alert, AlertDescription, AlertTitle } from './shadcn/alert'

const UserProfileDialogPlansAndBillingSection = ({
  subscriptionStatus,
  handleCancel,
  handleUpgrade,
}: {
  subscriptionStatus: SUBSCRIPTION_STATUSES
  handleCancel: () => void
  handleUpgrade: (plan: PlanDetail) => void
}) => {
  const annualFlagEnabled = useFeatureFlagEnabled(featureFlagKeys.annualPlans)

  const { userLimits, currentPlanConfigs, permissions: permissionData } = useUserPermissions()

  const [period, setPeriod] = useState<'monthly' | 'annually'>('monthly')

  const { data: chargebeeSyncStatus } = trpc.getIsUserSynced.useQuery(undefined, {
    refetchOnWindowFocus: false,
  })

  const { data: scheduledSubscriptionChanges } = trpc.getScheduledSubscriptionChanges.useQuery(undefined, {
    refetchOnWindowFocus: false,
  })

  const { data: userActiveSubscriptions } = trpc.getUserActiveSubscriptions.useQuery(undefined, {
    refetchOnWindowFocus: false,
  })

  const { mutate: openPortalSessionUrl, isPending: fetchingPortalSessionUrl } = trpc.getPortalSessionUrl.useMutation({
    onSuccess: async (data) => {
      if (data) {
        window.location.href = data
      }
    },
  })

  const userOnFreePlan =
    permissionData &&
    permissionData?.usersPlans.length > 0 &&
    permissionData?.usersPlans.some((plan) => [FREE_PLAN.plan_id].includes(plan))

  const userOnLifetimePlan =
    permissionData &&
    permissionData?.usersPlans.length > 0 &&
    permissionData?.usersPlans.some((plan) => [VISION_PASS.plan_id, DROP_SHIPPER.plan_id].includes(plan))

  const { usersPlans = [] } = permissionData ?? {}

  const onlyShowSpecialPlans = usersPlans.includes(VISION_PASS.plan_id) || usersPlans.includes(DROP_SHIPPER.plan_id)

  const plansToShow = onlyShowSpecialPlans
    ? [VISION_PASS, DROP_SHIPPER].filter((plan) => usersPlans.includes(plan.plan_id))
    : [...(usersPlans.includes(FREE_PLAN.plan_id) ? [FREE_PLAN] : []), SPARK_PLAN, STANDARD_PLAN, PRO_PLAN].filter(
        (plan): plan is PlanDetail => plan !== undefined,
      )

  const getUpgradeText = useCallback(
    (index: number) => {
      const userPlan = usersPlans[0]
      const userPlanIndex = plansToShow.findIndex((plan) => plan.plan_id === userPlan)
      if (userPlanIndex === -1) {
        return 'Upgrade'
      }
      if (index < userPlanIndex) {
        return 'Downgrade'
      }
      return 'Upgrade'
    },
    [usersPlans, plansToShow],
  )

  return (
    <div className={'space-y-6 p-6'}>
      <div className={'space-y-2'}>
        <Text size={'xl'} weight={'semibold'} className="leading-0">
          Your Plan{' '}
        </Text>{' '}
        <p className={'text-thememutedforeground'}>
          {subscriptionStatus === 'hasNoPlan'
            ? 'You are not currently on a Paid Plan. Choose a plan from below.'
            : null}
        </p>
      </div>
      <div className={'space-y-3'}>
        <div className={'flex justify-between gap-5 items-center'}>
          <span className={'font-semibold'}>Available Plans</span>
          {annualFlagEnabled ? (
            <Tabs defaultValue={period}>
              <TabsList className={'rounded-full'}>
                <TabsTrigger className={'rounded-full'} value={'monthly'} onClick={() => setPeriod('monthly')}>
                  Pay Monthly
                </TabsTrigger>
                <TabsTrigger
                  className={'rounded-full flex gap-1.5'}
                  value={'annually'}
                  onClick={() => setPeriod('annually')}
                >
                  <span>Pay Yearly</span> <span className={'text-themedestructive'}>Save 50%</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          ) : null}
        </div>
        <div>
          {scheduledSubscriptionChanges && (
            <Alert>
              <Message className="h-4 w-4" />
              <AlertTitle>Heads up!</AlertTitle>
              <AlertDescription>
                Your subscription will automatically switch to the{' '}
                {
                  plans.find(
                    (item) =>
                      item.no_trial_plan_id === scheduledSubscriptionChanges.plan ||
                      item.plan_id === scheduledSubscriptionChanges.plan,
                  )?.name
                }{' '}
                on{' '}
                {new Date(scheduledSubscriptionChanges.scheduledAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
                . Rest assured, no charges will be made.
              </AlertDescription>
            </Alert>
          )}
        </div>
        {plansToShow.map((plan, index) => (
          <UpgradeBox
            boxData={plan}
            key={index}
            upgradeText={getUpgradeText(index)}
            userSubscription={userActiveSubscriptions?.find((subscription) =>
              subscription.plans.includes(plan.plan_id || plan.no_trial_plan_id),
            )}
            period={period}
            userPlans={usersPlans}
            descriptionDetailsToShow="features"
            handleUpgrade={handleUpgrade}
          />
        ))}
      </div>
      <div>
        {chargebeeSyncStatus && subscriptionStatus === 'hasPlan' && (
          <div className={'flex justify-between items-center gap-1.5 h-9'}>
            {fetchingPortalSessionUrl ? (
              <span className="font-medium text-sm leading-5 text-thememutedforeground tracking-wide	">
                Loading...
              </span>
            ) : (
              <div className="flex gap-2">
                <Button size={'sm'} onClick={() => openPortalSessionUrl()}>
                  Manage Billing
                </Button>
                {!(userOnFreePlan || userOnLifetimePlan) && (
                  <Button variant={'outline'} onClick={() => handleCancel()}>
                    Cancel Subscription
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div>
        <p className={'text-sm font-light text-thememutedforeground'}>
          Learn more about{' '}
          <a href={'https://creativeos.com/#pricing'} className={'underline'}>
            all plans & features
          </a>
          .
        </p>
      </div>
      {currentPlanConfigs.length > 0 && permissionData ? (
        <Stack className="gap-3 w-full">
          <Text size={'base'} weight={'semibold'}>
            Features
          </Text>
          <Stack className="gap-0.5">
            <div className="flex gap-1 justify-between">
              <Text size={'sm'} weight={'normal'}>
                Ad Template Requests
              </Text>
              <Text size={'sm'} weight={'semibold'} className="text-right">
                {currentPlanConfigs[0].requestLimits.ads == 'unlimited'
                  ? 'Unlimited'
                  : `${Math.max(userLimits?.request.ad ?? 0, 0)}/${currentPlanConfigs[0].requestLimits.ads} Remaining`}
              </Text>
            </div>
            <Text size={'xs'} weight={'thin'} className="text-thememutedforeground">
              Your plan allows for {currentPlanConfigs[0].requestLimits.ads} Ad Template Requests/month. Resets on{' '}
              {new Date(permissionData?.limitNextUpdateAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
              .
            </Text>
          </Stack>
          <Stack className="gap-0.5">
            <div className="flex gap-1 justify-between">
              <Text size={'sm'} weight={'normal'}>
                Email Template Requests
              </Text>
              <Text size={'sm'} weight={'semibold'} className="text-right">
                {currentPlanConfigs[0].requestLimits.emails == 'unlimited'
                  ? 'Unlimited'
                  : `${Math.max(userLimits?.request.email ?? 0, 0)}/${currentPlanConfigs[0].requestLimits.emails} Remaining`}
              </Text>
            </div>
            <Text size={'xs'} weight={'thin'} className="text-thememutedforeground">
              Your plan allows for {currentPlanConfigs[0].requestLimits.emails} Email Template Requests/month. Resets on{' '}
              {new Date(permissionData?.limitNextUpdateAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
              .
            </Text>
          </Stack>
          <Stack className="gap-0.5">
            <div className="flex gap-1 justify-between">
              <Text size={'sm'} weight={'normal'}>
                Lander Template Requests
              </Text>
              <Text size={'sm'} weight={'semibold'} className="text-right">
                {currentPlanConfigs[0].requestLimits.lander == 'unlimited'
                  ? 'Unlimited'
                  : `${Math.max(userLimits?.request.lander ?? 0, 0)}/${currentPlanConfigs[0].requestLimits.lander} Remaining`}
              </Text>
            </div>
            <Text size={'xs'} weight={'thin'} className="text-thememutedforeground">
              Your plan allows for {currentPlanConfigs[0].requestLimits.lander} Lander Template Requests/month. Resets
              on{' '}
              {new Date(permissionData?.limitNextUpdateAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
              .
            </Text>
          </Stack>
        </Stack>
      ) : null}
    </div>
  )
}

export default UserProfileDialogPlansAndBillingSection
